'use client'

import { Badge, PersonIdentityCell, useInviteToApply } from '@yeshuman/sdk'
import { type FormEvent, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { InviteByEmailField } from './invite-by-email-field'
import {
  checkEmailInvite,
  useEmailInviteMutations,
  type EmailInviteCheckResponse,
} from '../../hooks/use-opportunity-email-invites'

type InviteApplicantsByEmailProps = {
  opportunityId: string
}

/** Job Invite-tab email invite: shared input shell + job-specific check / match / create. */
export function InviteApplicantsByEmail({ opportunityId }: InviteApplicantsByEmailProps) {
  const [email, setEmail] = useState('')
  const [checkResult, setCheckResult] = useState<EmailInviteCheckResponse | null>(null)
  const [checking, setChecking] = useState(false)
  const [checkError, setCheckError] = useState<string | null>(null)
  const { createInvite } = useEmailInviteMutations(opportunityId)
  const { inviteProfile, isInviting } = useInviteToApply(opportunityId)

  const matchedProfile = checkResult?.matched_profile ?? null
  const pendingInvite = checkResult?.pending_invite ?? null
  const firstName = matchedProfile?.name?.split(' ')[0] ?? 'candidate'
  const isSubmitting = createInvite.isPending || isInviting

  const submitLabel = pendingInvite
    ? 'Invite already pending'
    : matchedProfile
      ? `Invite ${firstName} directly`
      : 'Send invite'

  const ensureCheck = async (trimmed: string): Promise<EmailInviteCheckResponse | null> => {
    setChecking(true)
    setCheckError(null)
    try {
      const result = await checkEmailInvite(opportunityId, trimmed)
      setCheckResult(result)
      return result
    } catch (error) {
      setCheckResult(null)
      setCheckError(error instanceof Error ? error.message : 'Failed to check email')
      return null
    } finally {
      setChecking(false)
    }
  }

  const handleEmailChange = (next: string) => {
    setEmail(next)
    setCheckResult(null)
    setCheckError(null)
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const trimmed = email.trim()
    if (!trimmed) {
      toast.error('Enter an email address')
      return
    }
    if (pendingInvite) return

    const result = checkResult ?? (await ensureCheck(trimmed))
    if (!result || result.pending_invite) return

    if (result.matched_profile) {
      inviteProfile(result.matched_profile.id)
      setEmail('')
      setCheckResult(null)
      return
    }

    try {
      await createInvite.mutateAsync(trimmed)
      setEmail('')
      setCheckResult(null)
    } catch {
      // toast handled in hook
    }
  }

  return (
    <InviteByEmailField
      value={email}
      onChange={handleEmailChange}
      onSubmit={(event) => void handleSubmit(event)}
      onEmailBlur={() => {
        const trimmed = email.trim()
        if (trimmed) void ensureCheck(trimmed)
      }}
      isSubmitting={isSubmitting}
      submitDisabled={Boolean(pendingInvite) || checking}
      submitLabel={submitLabel}
      inputId="invite-applicants-email"
    >
      {checking ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
          <Loader2 className="size-4 animate-spin" aria-hidden />
          Checking…
        </div>
      ) : null}

      {checkError ? <p className="text-sm text-destructive">{checkError}</p> : null}

      {pendingInvite ? (
        <p className="text-sm text-muted-foreground">
          A pending invite for this email was sent{' '}
          {new Date(pendingInvite.created_at).toLocaleDateString()}. Resend or revoke it from the
          sourcing list below.
        </p>
      ) : null}

      {matchedProfile && !pendingInvite ? (
        <div className="rounded-md border border-primary/30 bg-primary/5 p-3">
          <PersonIdentityCell
            name={matchedProfile.name}
            subline={matchedProfile.headline || undefined}
            avatarUrl={matchedProfile.avatar_url}
            colorSeed={matchedProfile.id}
            nameAside={
              <Badge variant="secondary" className="font-normal">
                Existing member
              </Badge>
            }
          />
        </div>
      ) : null}

      {!matchedProfile && !pendingInvite && checkResult && email.trim() ? (
        <p className="text-sm text-muted-foreground">
          They&apos;ll join the pipeline when they sign up.
        </p>
      ) : null}
    </InviteByEmailField>
  )
}
